import { io } from "socket.io-client";
import jwtDecode from "jwt-decode";
import { store } from ".";
import { setSocketStatus } from "./socket-slice";
import { updateBalance } from "./auth-slice";
import { API_URL } from "../constants";
import { updateOdds } from "./odd-slice";
import { handleOddsChange, updateMarketStatus } from "./betslipold-slice";

class SocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.baseDelay = 1000;
  }

  connect(userId) {
    if (this.socket?.connected) return;

    // Initialize socket with auth token
    this.socket = io(API_URL, {
      auth: { userId },
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.baseDelay,
      reconnectionDelayMax: 30000, // 30 seconds max delay
      timeout: 20000,
      transports: ["polling"],
    });

    // Connection established
    this.socket.on("connect", () => {
      this.reconnectAttempts = 0;
      store.dispatch(setSocketStatus("connected"));
      console.log("Socket.IO connected");
    });

    // Balance update event
    this.socket.on("balance_update", (data) => {
      console.log("BALANCE UPDATE", data);
      console.log("balance_update", data.balance);
      store.dispatch(
        updateBalance({
          balance: data.balance,
        })
      );
    });

    // 1. Single Bet Odds Change Listener
    this.socket.on("odds-update", (data) => {
      console.log("ODDS CHANGE EVENT", data);

      const {
        matchId,
        marketName,
        oddLabel,
        newOdds,
        tradingStatus = "Open",
        fixtureId,
        selectionId, // Now we have selectionId from server
        timestamp,
      } = data;

      // Update the main odds store
      // if (fixtureId && selectionId && newOdds) {
      //   store.dispatch(
      //     updateOdds({
      //       fixtureId,
      //       selectionId,
      //       odds: { [oddLabel]: newOdds },
      //       marketInfo: { marketName },
      //       timestamp: timestamp || Date.now(),
      //     })
      //   );
      // }
      // Update betslip if this selection is in the betslip
      // Now we don't need oldOdds since we track it in the betslip state
      store.dispatch(
        handleOddsChange({
          matchId,
          marketName,
          oddLabel,
          newOdds,
          tradingStatus,
          selectionId, // Pass selectionId for precise bet identification
        })
      );
    });

    // 2. Bulk Odds Changes Listener
    this.socket.on("bulk_odds_update", (data) => {
      console.log("BULK ODDS UPDATE", data);

      const { changes } = data;

      changes.forEach((change) => {
        const {
          matchId,
          fixtureId,
          marketName,
          oddLabel,
          newOdds,
          oldOdds,
          tradingStatus = "Open",
        } = change;

        // Update main odds store
        if (fixtureId && newOdds) {
          store.dispatch(
            updateOdds({
              fixtureId,
              odds: { [oddLabel]: newOdds },
            })
          );
        }

        // Update betslip for each change
        store.dispatch(
          handleOddsChange({
            matchId,
            marketName,
            oddLabel,
            newOdds,
            oldOdds,
            tradingStatus,
          })
        );
      });
    });

    // 3. Trading Status Change Listener
    this.socket.on("market_status_change", (data) => {
      console.log("MARKET STATUS CHANGE", data);

      const { matchId, marketName, newStatus, oldStatus, eventId, reason } =
        data;

      // Update betslip market status
      store.dispatch(
        updateMarketStatus({
          matchId,
          marketName,
          newStatus,
          eventId,
        })
      );

      // You can also update your main odds store to reflect status changes
      // This depends on your odds store structure
      console.log(
        `Market ${marketName} status changed from ${oldStatus} to ${newStatus}${
          reason ? ` - Reason: ${reason}` : ""
        }`
      );
    });

    // Connection error
    this.socket.on("connect_error", (err) => {
      console.error("Socket connection error:", err.message);
      store.dispatch(setSocketStatus("error"));
    });

    // Reconnecting
    this.socket.on("reconnecting", (attempt) => {
      this.reconnectAttempts = attempt;
      store.dispatch(
        setSocketStatus(
          `reconnecting (${attempt}/${this.maxReconnectAttempts})`
        )
      );
    });

    // Disconnected
    this.socket.on("disconnect", (reason) => {
      store.dispatch(setSocketStatus("disconnected"));
      if (reason === "io server disconnect") {
        // The server explicitly disconnected, need to manually reconnect
        setTimeout(() => this.connect(userId), 1000);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      store.dispatch(setSocketStatus("disconnected"));
    }
  }

  // Manual balance refresh request
  requestBalanceRefresh(provider) {
    if (this.socket?.connected) {
      this.socket.emit("request_balance", { provider });
    }
  }

  // Subscribe to specific match odds updates
  subscribeToMatch(matchId) {
    if (this.socket?.connected) {
      this.socket.emit("subscribe_match", { matchId });
    }
  }

  // Unsubscribe from specific match odds updates
  unsubscribeFromMatch(matchId) {
    if (this.socket?.connected) {
      this.socket.emit("unsubscribe_match", { matchId });
    }
  }

  // Subscribe to specific market updates
  subscribeToMarket(matchId, marketName) {
    if (this.socket?.connected) {
      this.socket.emit("subscribe_market", { matchId, marketName });
    }
  }
}

export const socketService = new SocketService();
